/* =========================================================
   CAPRI — capri.js
   Behaviour for the Capri sections (ported from the prototype's section init() functions).
   Markup is rendered by Liquid; this file only adds interactions. Re-runs in the theme editor
   on shopify:section:load.
   ========================================================= */
(() => {
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pad2 = (n) => String(n).padStart(2, '0');
  const money = (cents) => {
    const c = Math.round(cents);
    return `${Math.floor(c / 100)}.${String((c % 100) * 10).padStart(3, '0')} DT`;
  };
  const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const ICON = {
    play: '<svg class="cp-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7 4.5v15a.5.5 0 0 0 .76.43l12.5-7.5a.5.5 0 0 0 0-.86L7.76 4.07A.5.5 0 0 0 7 4.5Z"/></svg>',
    pause: '<svg class="cp-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="4.5" width="4" height="15" rx="1" fill="currentColor"/><rect x="14" y="4.5" width="4" height="15" rx="1" fill="currentColor"/></svg>',
    volOn: '<svg class="cp-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H2v6h4l5 4V5Z"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>',
    volOff: '<svg class="cp-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H2v6h4l5 4V5Z"/><path d="m22 9-6 6M16 9l6 6"/></svg>',
    chevron: '<svg class="cp-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>',
  };

  /* ---------- Reveal on scroll ---------- */
  let revealIO;
  function initReveal(root) {
    const els = root.querySelectorAll('[data-reveal]:not(.is-visible)');
    if (!('IntersectionObserver' in window) || reduced() || window.Shopify?.designMode) {
      els.forEach((e) => e.classList.add('is-visible'));
      return;
    }
    revealIO = revealIO || new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) {
          en.target.classList.add('is-visible');
          revealIO.unobserve(en.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach((e) => revealIO.observe(e));
  }

  /* ---------- Muted autoplay while visible ---------- */
  let videoIO;
  function autoplayWhenVisible(video, target = video) {
    if (!video) return;
    video.muted = true;
    video.setAttribute('playsinline', '');
    videoIO = videoIO || new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const v = en.target.__cpVideo;
        if (!v) return;
        en.isIntersecting ? v.play().catch(() => {}) : v.pause();
      });
    }, { threshold: 0.35 });
    target.__cpVideo = video;
    videoIO.observe(target);
  }

  /* ---------- Horizontal scroll progress ---------- */
  function initScrollProgress(root) {
    const sc = root.querySelector('[data-scroller]');
    const bar = root.querySelector('[data-progress]');
    if (!sc || !bar) return;
    const upd = () => {
      const max = sc.scrollWidth - sc.clientWidth;
      const w = Math.min(1, sc.clientWidth / Math.max(1, sc.scrollWidth));
      bar.style.width = `${w * 100}%`;
      bar.style.transform = `translateX(${max > 0 ? (sc.scrollLeft / max) * ((1 - w) / w) * 100 : 0}%)`;
    };
    sc.addEventListener('scroll', upd, { passive: true });
    window.addEventListener('resize', upd);
    upd();
  }

  /* ---------- Announcement bar ---------- */
  function initAnnouncement(el) {
    const items = [...el.querySelectorAll('.cp-announcement__msg')];
    if (items.length < 2 || reduced()) return;
    let i = 0;
    clearInterval(el.__cpTimer);
    el.__cpTimer = setInterval(() => {
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, (Number(el.dataset.speed) || 4) * 1000);
  }

  /* ---------- Header ---------- */
  function initHeader(el) {
    const header = el.querySelector('[data-header]');
    const template = document.querySelector('main[data-template]')?.dataset.template || '';
    const firstSection = document.querySelector('main > .shopify-section');
    const transparent = el.dataset.transparent === 'true' && template === 'index' && firstSection?.classList.contains('cp-section-hero-video');
    const wrapper = el.closest('.shopify-section');
    if (el.classList.contains('is-static')) wrapper?.classList.add('is-static');
    document.body.classList.toggle('has-transparent-header', !!transparent);
    const onScroll = () => header.classList.toggle('is-solid', !transparent || window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const menu = el.querySelector('.cp-mobile-menu');
    const openBtn = el.querySelector('[data-menu-open]');
    const setMenu = (open) => {
      menu.classList.toggle('is-open', open);
      menu.setAttribute('aria-hidden', String(!open));
      openBtn?.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('is-locked', open);
    };
    openBtn?.addEventListener('click', () => setMenu(true));
    el.querySelector('[data-menu-close]')?.addEventListener('click', () => setMenu(false));
    document.addEventListener('keydown', (e) => e.key === 'Escape' && setMenu(false));
  }

  /* ---------- UGC video wall ---------- */
  function embedUrl(link) {
    let m;
    if ((m = link.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/))) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&playsinline=1`;
    if ((m = link.match(/vimeo\.com\/(\d+)/))) return `https://player.vimeo.com/video/${m[1]}?autoplay=1`;
    if ((m = link.match(/instagram\.com\/(?:p|reel)\/([\w-]+)/))) return `https://www.instagram.com/reel/${m[1]}/embed`;
    if ((m = link.match(/tiktok\.com\/.*video\/(\d+)/))) return `https://www.tiktok.com/embed/v2/${m[1]}`;
    return null;
  }
  function initUgc(el) {
    initScrollProgress(el);
    if (el.dataset.autoplay === 'true') el.querySelectorAll('.cp-ugc-card__media video').forEach((v) => autoplayWhenVisible(v));
    const modal = el.querySelector('[data-ugc-modal]');
    const stage = el.querySelector('[data-ugc-stage]');
    const close = () => { modal.hidden = true; stage.innerHTML = ''; document.body.classList.remove('is-locked'); };
    el.addEventListener('click', (e) => {
      const mute = e.target.closest('[data-ugc-mute]');
      if (mute) {
        const v = mute.closest('.cp-ugc-card').querySelector('.cp-ugc-card__media video');
        const on = mute.getAttribute('aria-pressed') !== 'true';
        mute.setAttribute('aria-pressed', String(on));
        mute.setAttribute('aria-label', on ? 'Couper le son' : 'Activer le son');
        mute.innerHTML = on ? ICON.volOn : ICON.volOff;
        if (v) { v.muted = !on; v.play().catch(() => {}); }
        return;
      }
      const card = e.target.closest('.cp-ugc-card');
      if (!card || e.target.closest('a')) return;
      if (!e.target.closest('[data-ugc-open]') && !e.target.closest('.cp-ugc-card__media')) return;
      const tpl = card.querySelector('template[data-ugc-modal-content]');
      stage.innerHTML = tpl ? tpl.innerHTML : '';
      stage.querySelectorAll('[data-embed-link]').forEach((d) => {
        const url = embedUrl(d.dataset.embedLink);
        d.outerHTML = url ? `<iframe src="${esc(url)}" title="Vidéo" allow="autoplay; fullscreen; encrypted-media" allowfullscreen></iframe>` : '';
      });
      modal.hidden = false;
      document.body.classList.add('is-locked');
      modal.querySelector('[data-ugc-close]').focus();
    });
    modal.querySelector('[data-ugc-close]').addEventListener('click', close);
    modal.addEventListener('click', (e) => e.target === modal && close());
    document.addEventListener('keydown', (e) => e.key === 'Escape' && !modal.hidden && close());
  }

  /* ---------- Unboxing video ---------- */
  function initUnboxing(el) {
    el.querySelectorAll('.cp-ub__frame').forEach((frame) => {
      const video = frame.querySelector('video');
      if (!video) return;
      video.loop = true;
      if (el.dataset.autoplay === 'true') autoplayWhenVisible(video, frame);
      const play = frame.querySelector('[data-ub-play]');
      const mute = frame.querySelector('[data-ub-mute]');
      if (!play) return;
      const sync = () => {
        play.innerHTML = video.paused ? ICON.play : ICON.pause;
        play.setAttribute('aria-label', video.paused ? 'Lecture' : 'Pause');
      };
      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      play.addEventListener('click', () => (video.paused ? video.play() : video.pause()));
      mute.addEventListener('click', () => {
        video.muted = !video.muted;
        mute.setAttribute('aria-pressed', String(!video.muted));
        mute.setAttribute('aria-label', video.muted ? 'Activer le son' : 'Couper le son');
        mute.innerHTML = video.muted ? ICON.volOff : ICON.volOn;
      });
      sync();
    });
  }

  /* ---------- FAQ accordion ---------- */
  function initFaq(el) {
    el.querySelectorAll('.cp-accordion__btn').forEach((btn) =>
      btn.addEventListener('click', () => {
        const item = btn.closest('.cp-accordion__item');
        const open = !item.classList.contains('is-open');
        item.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', String(open));
      }));
  }

  /* ---------- Product page: gallery + bundle + add to cart ---------- */
  function initProduct(el) {
    // Gallery
    const track = el.querySelector('[data-track]');
    const thumbs = el.querySelector('[data-thumbs]');
    const counter = el.querySelector('[data-counter]');
    if (track) {
      const total = track.children.length;
      const current = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      const go = (i) => track.scrollTo({ left: i * track.clientWidth, behavior: reduced() ? 'auto' : 'smooth' });
      const sync = () => {
        const i = current();
        if (counter) counter.textContent = `${pad2(i + 1)} / ${pad2(total)}`;
        thumbs?.querySelectorAll('[data-thumb]').forEach((t, j) => t.classList.toggle('is-active', j === i));
      };
      track.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
      thumbs?.addEventListener('click', (e) => { const t = e.target.closest('[data-thumb]'); if (t) go(Number(t.dataset.thumb)); });
      el.querySelectorAll('[data-nav]').forEach((b) => b.addEventListener('click', () => go(Math.max(0, Math.min(total - 1, current() + Number(b.dataset.nav))))));
    }

    // Variant picker (only on products with options): updates price, pair 1 and add-to-cart
    const variantSelect = el.querySelector('[data-variant-select]');
    const currentScript = el.querySelector('[data-current-product]');
    variantSelect?.addEventListener('change', () => {
      const opt = variantSelect.selectedOptions[0];
      const price = Number(opt.dataset.price);
      const priceEl = el.querySelector('[data-price-display]');
      if (priceEl) priceEl.textContent = money(price);
      const compare = Number(opt.dataset.compare) || 0;
      const compareEl = el.querySelector('[data-compare-display]');
      if (compareEl) {
        compareEl.hidden = !(compare > price);
        compareEl.textContent = compare > price ? money(compare) : '';
      }
      if (currentScript) {
        const cur = JSON.parse(currentScript.textContent);
        cur.variant_id = Number(variantSelect.value);
        cur.price = price;
        currentScript.textContent = JSON.stringify(cur);
      }
      el.dispatchEvent(new CustomEvent('capri:variant', { detail: { id: Number(variantSelect.value), price } }));
    });

    // Bundle selector (visual layer — see comment in sections/capri-main-product.liquid)
    const bundle = el.querySelector('[data-bundle]');
    const recap = el.querySelector('[data-order-recap]');
    let order = null;
    if (bundle) {
      const products = JSON.parse(bundle.querySelector('[data-bundle-products]')?.textContent || '[]');
      const current = JSON.parse(bundle.querySelector('[data-current-product]')?.textContent || 'null');
      if (current && !products.some((p) => p.handle === current.handle)) products.unshift(current);
      const prod = (h) => (h === current.handle ? current : products.find((p) => p.handle === h) || current);
      el.addEventListener('capri:variant', (e) => { current.variant_id = e.detail.id; current.price = e.detail.price; render(); });
      const pairsEl = bundle.querySelector('[data-pairs]');
      const hidden = bundle.querySelector('#capri-bundle-summary');
      const pairLabel = bundle.dataset.pairLabel || 'Paire';
      const radios = [...bundle.querySelectorAll('input[type="radio"]')];
      const state = { tier: Math.max(0, radios.findIndex((r) => r.checked)), pairs: [current.handle] };

      const pairRow = (i) => `<div class="cp-pair" data-pair="${i}">
          <span class="cp-pair__label cp-mono">${esc(pairLabel)} ${i + 1}</span>
          <div class="cp-select"><select aria-label="${esc(pairLabel)} ${i + 1}" data-pair-model>${products
            .map((p) => `<option value="${esc(p.handle)}" ${p.handle === state.pairs[i] ? 'selected' : ''} ${p.available ? '' : 'disabled'}>${esc(p.title)}${p.available ? '' : ' (épuisé)'}</option>`)
            .join('')}</select>${ICON.chevron}</div>
        </div>`;

      const render = () => {
        const r = radios[state.tier];
        const qty = Number(r.dataset.qty) || 1;
        while (state.pairs.length < qty) state.pairs.push(state.pairs[state.pairs.length - 1]);
        bundle.querySelectorAll('.cp-tier').forEach((c, i) => c.classList.toggle('is-selected', i === state.tier));
        r.checked = true;
        if (qty > 1) {
          pairsEl.hidden = false;
          pairsEl.innerHTML = `<p class="cp-bundle__pairs-title">Choisissez vos ${qty} paires</p>` + Array.from({ length: qty }, (_, i) => pairRow(i)).join('');
        } else {
          pairsEl.hidden = true;
          pairsEl.innerHTML = '';
        }
        const pairs = state.pairs.slice(0, qty).map((h) => { const p = prod(h); return { handle: p.handle, title: p.title, variant_id: p.variant_id }; });
        const total = Number(r.dataset.total);
        order = { tier: state.tier + 1, tier_title: r.dataset.title, quantity: qty, pairs, total, total_formatted: money(total) };
        window.CapriOrder = order;
        hidden.value = JSON.stringify(order);
        if (recap) recap.innerHTML = `<span class="cp-mono">Votre sélection</span><span>${qty} paire${qty > 1 ? 's' : ''} · ${pairs.map((x) => esc(x.title)).join(' + ')}</span><strong>${order.total_formatted}</strong>`;
        document.dispatchEvent(new CustomEvent('capri:order-change', { detail: order }));
      };
      bundle.addEventListener('change', (e) => {
        const t = e.target;
        if (t.type === 'radio') state.tier = radios.indexOf(t);
        const row = t.closest('[data-pair]');
        if (row) state.pairs[Number(row.dataset.pair)] = t.value;
        render();
      });
      render();

      // "ou X DT la paire avec l'offre 3 paires" link: select that tier
      el.querySelector('[data-hint]')?.addEventListener('click', () => {
        let best = -1; let bestPer = Infinity;
        radios.forEach((r, i) => { const q = Number(r.dataset.qty); const per = Number(r.dataset.total) / q; if (q > 1 && per < bestPer) { bestPer = per; best = i; } });
        if (best >= 0) { radios[best].checked = true; state.tier = best; render(); }
      });
    }

    // Add to cart (secondary path; the COD app is the main path). Adds every pair, opens Dawn's cart drawer.
    const atc = el.querySelector('[data-add-to-cart]');
    atc?.addEventListener('click', async () => {
      const current = JSON.parse(el.querySelector('[data-current-product]')?.textContent || 'null');
      const pairs = order ? order.pairs : current ? [{ variant_id: current.variant_id, title: current.title }] : [];
      if (!pairs.length) return;
      const counts = {};
      pairs.forEach((p) => { counts[p.variant_id] = (counts[p.variant_id] || 0) + 1; });
      const items = Object.entries(counts).map(([id, quantity]) => ({
        id: Number(id), quantity,
        properties: order && order.quantity > 1 ? { _Offre: order.tier_title } : {},
      }));
      const drawer = document.querySelector('cart-drawer');
      atc.disabled = true;
      try {
        const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: drawer ? ['cart-drawer', 'cart-icon-bubble'] : [], sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || 'cart');
        if (drawer && data.sections) {
          drawer.classList.remove('is-empty'); // same as Dawn's product-form.js
          drawer.renderContents(data);
        }
        else window.location.href = window.routes?.cart_url || '/cart';
      } catch (err) {
        console.error(err);
        window.location.href = window.routes?.cart_url || '/cart';
      } finally {
        atc.disabled = false;
      }
    });
  }

  /* ---------- Sticky buy bar ---------- */
  function initSticky(el) {
    const bar = el.querySelector('[data-sticky]');
    const buy = document.querySelector('[data-buy-area]');
    if (!bar || !buy) return;
    const price = el.querySelector('[data-sticky-price]');
    if (window.CapriOrder) price.textContent = window.CapriOrder.total_formatted;
    document.addEventListener('capri:order-change', (e) => { price.textContent = e.detail.total_formatted; });
    let shown = false;
    const update = () => {
      const passed = buy.getBoundingClientRect().bottom < 0;
      if (passed === shown) return;
      shown = passed;
      bar.classList.toggle('is-visible', shown);
      bar.setAttribute('aria-hidden', String(!shown));
      bar.querySelector('[data-sticky-go]').tabIndex = shown ? 0 : -1;
      document.body.classList.toggle('has-sticky-bar', shown);
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ---------- Collection: tag filters + sort on the rendered cards ---------- */
  function initCollection(el) {
    const grid = el.querySelector('[data-grid]');
    if (!grid) return;
    const cards = [...grid.querySelectorAll('.cp-card')];
    const original = cards.slice();
    const active = { couleur: new Set(), forme: new Set() };
    let sort = el.dataset.defaultSort || 'featured';
    const paint = () => {
      let list = original.filter((c) => {
        const tags = (c.dataset.tags || '').split(',');
        return Object.entries(active).every(([g, set]) => !set.size || [...set].some((v) => tags.includes(`${g}:${v}`)));
      });
      const by = {
        'price-asc': (a, b) => a.dataset.price - b.dataset.price,
        'price-desc': (a, b) => b.dataset.price - a.dataset.price,
        'title-asc': (a, b) => a.dataset.title.localeCompare(b.dataset.title, 'fr'),
      }[sort];
      if (by) list = list.slice().sort(by);
      original.forEach((c) => { c.hidden = !list.includes(c); });
      list.forEach((c) => { grid.appendChild(c); c.classList.add('is-visible'); });
      el.querySelector('[data-count]').textContent = `${pad2(list.length)} modèle${list.length > 1 ? 's' : ''}`;
      el.querySelector('[data-empty]').hidden = list.length > 0;
      el.querySelector('[data-reset]').hidden = !active.couleur.size && !active.forme.size;
    };
    el.addEventListener('click', (e) => {
      const c = e.target.closest('[data-group]');
      if (c) {
        const set = active[c.dataset.group];
        set.has(c.dataset.value) ? set.delete(c.dataset.value) : set.add(c.dataset.value);
        c.classList.toggle('is-active');
        c.setAttribute('aria-pressed', String(c.classList.contains('is-active')));
        paint();
      }
      if (e.target.closest('[data-reset]')) {
        active.couleur.clear(); active.forme.clear();
        el.querySelectorAll('[data-group]').forEach((x) => { x.classList.remove('is-active'); x.setAttribute('aria-pressed', 'false'); });
        paint();
      }
    });
    el.querySelector('[data-sort]')?.addEventListener('change', (e) => { sort = e.target.value; paint(); });
    if (sort !== 'featured') paint();
  }

  /* ---------- Thank-you page: WhatsApp link from the floating button's number ---------- */
  function initThanks(el) {
    const link = el.querySelector('[data-ty-wa]');
    const number = document.querySelector('[data-cp-wa-number]')?.dataset.cpWaNumber;
    if (link && number) link.href = `https://wa.me/${number}?text=${encodeURIComponent("Bonjour CAPRI, j'ai une question sur ma commande.")}`;
    else if (link) link.hidden = true;
  }

  /* ---------- Boot ---------- */
  const INIT = [
    ['[data-cp-announcement]', initAnnouncement],
    ['[data-cp-header]', initHeader],
    ['[data-cp-scroller-section]', (el) => { if (!el.matches('[data-cp-ugc]')) initScrollProgress(el); }],
    ['[data-cp-ugc]', initUgc],
    ['[data-cp-unboxing]', initUnboxing],
    ['[data-cp-faq]', initFaq],
    ['[data-cp-product]', initProduct],
    ['[data-cp-sticky]', initSticky],
    ['[data-cp-collection]', initCollection],
    ['[data-cp-thanks]', initThanks],
  ];
  function init(root = document) {
    INIT.forEach(([sel, fn]) => {
      const nodes = root.matches?.(sel) ? [root] : [];
      nodes.concat([...root.querySelectorAll(sel)]).forEach((el) => {
        if (el.__cpInit?.includes(fn)) return;
        el.__cpInit = (el.__cpInit || []).concat(fn);
        try { fn(el); } catch (e) { console.error('[capri]', sel, e); }
      });
    });
    initReveal(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
